import { MediaPlayer, MediaPlayerInstance, MediaProvider } from '@vidstack/react';
import { DefaultAudioLayout, defaultLayoutIcons } from '@vidstack/react/player/layouts/default';
import { FunctionComponent, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useCookies } from 'react-cookie';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { AudioPlayerContext, DetailsModalContext } from '../../../context';
import { AUDIO_PLAYER_COOKIE_KEY } from '../../constants';
import { useAnalytics, useBeforeUnload, useDuration, usePrevious } from '../../hooks';
import { cookieOptions, isModalOpened, layoutPath } from '../../utils';
import { CloseButton } from '../CloseButton';

import classNames from 'classnames';
import classes from './AudioPlayer.module.scss';

export const AudioPlayer: FunctionComponent = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { pathname } = useLocation();

  const [cookies, setCookie] = useCookies([AUDIO_PLAYER_COOKIE_KEY]);

  const { logItemPlayed, logPostAudioPlayed, logClickOpenArticle } = useAnalytics();

  const { startTrackTime, stopTrackTime, getDuration } = useDuration();

  const { setDetailsModalData } = useContext(DetailsModalContext);

  const { audioPlayerData, setAudioPlayerData } = useContext(AudioPlayerContext);

  const { userId, channelId, storyId, cardId, blockId, cardTitle, src, isPlaying, chatId } =
    audioPlayerData;

  const cardIdPrev = usePrevious(cardId) ?? 0;

  const playerRef = useRef<MediaPlayerInstance>(null);

  const playerElement = playerRef.current?.el?.querySelector('video, audio') as
    | HTMLVideoElement // used for HLS
    | HTMLAudioElement; // used for MP3

  const { playlist = {} } = { ...cookies[AUDIO_PLAYER_COOKIE_KEY] };

  const lastSecondRef = useRef<number>(-1);

  const setCurrentTime = useCallback(() => {
    if (!playerRef.current || !playerElement) {
      return;
    }

    if (playlist[`${cardId}_${src}`]) {
      playerRef.current.currentTime = playlist[`${cardId}_${src}`].currentTime ?? 0;
    }
  }, [cardId, playerElement, playlist, src]);

  const saveCurrentTime = useCallback(() => {
    if (!playerElement) {
      return;
    }

    const { currentTime, duration } = playerElement;

    if (!duration) {
      return;
    }

    const played = duration - currentTime < 1;

    playlist[`${cardId}_${src}`] = { played, currentTime: played ? 0 : Math.floor(currentTime) };

    setCookie(AUDIO_PLAYER_COOKIE_KEY, { playlist }, cookieOptions());
  }, [cardId, playerElement, playlist, setCookie, src]);

  const onPlayHandling = useCallback(() => {
    setCurrentTime();

    startTrackTime();

    if (Boolean(pathname !== layoutPath(`/details/${storyId}/${cardId}`) && !isModalOpened())) {
      logClickOpenArticle({
        user_Id: userId,
        channel_id: channelId,
        story_id: storyId,
        item_id: cardId,
        time_stamp: new Date().toISOString(),
      });
    }
  }, [
    cardId,
    channelId,
    logClickOpenArticle,
    pathname,
    setCurrentTime,
    startTrackTime,
    storyId,
    userId,
  ]);

  const onPauseHandling = useCallback(
    (cardId: number) => {
      const logArgs = {
        channel_id: channelId,
        story_id: storyId,
        item_id: cardId,
        time_stamp: new Date().toISOString(),
        duration: getDuration(),
      };

      if (blockId) {
        logPostAudioPlayed({ ...logArgs, block_id: blockId });
      } else {
        logItemPlayed({ ...logArgs, user_Id: userId });
      }

      stopTrackTime();
    },
    [
      blockId,
      channelId,
      getDuration,
      logItemPlayed,
      logPostAudioPlayed,
      stopTrackTime,
      storyId,
      userId,
    ]
  );

  useBeforeUnload(() => onPauseHandling(cardId));

  const isNewTrack = useMemo(
    () => Boolean(cardIdPrev && cardId !== cardIdPrev),
    [cardId, cardIdPrev]
  );

  useEffect(() => {
    if (!playerElement) {
      return;
    }

    if (src && isPlaying) {
      playerElement.play();

      if (!isNewTrack) {
        onPlayHandling();
      }

      return;
    }

    playerElement.pause();

    onPauseHandling(cardId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, src, playerElement]);

  const onClose = () => {
    saveCurrentTime();
    onPauseHandling(cardId);
    setAudioPlayerData({ ...audioPlayerData, src: '' });
  };

  const onPlay = useCallback(() => {
    setAudioPlayerData({ ...audioPlayerData, isPlaying: true });
  }, [audioPlayerData, setAudioPlayerData]);

  const onPause = useCallback(() => {
    setAudioPlayerData({ ...audioPlayerData, isPlaying: false });
  }, [audioPlayerData, setAudioPlayerData]);

  useEffect(() => {
    if (!cardId || !isNewTrack) {
      return;
    }

    onPauseHandling(cardIdPrev);

    onPlayHandling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId]);

  const titleClickHandler = useCallback(() => {
    isMobileOnly
      ? navigate(layoutPath(`/details/${storyId}/${cardId}`))
      : setDetailsModalData({ storyId, cardId, chatId });
  }, [cardId, chatId, navigate, setDetailsModalData, storyId]);

  const handleTimeUpdate = useCallback(() => {
    if (!playerElement) {
      return;
    }

    const currentTime = Math.floor(playerElement.currentTime);

    // Only run once per second, because onTimeUpdate fires too often
    if (currentTime !== lastSecondRef.current) {
      lastSecondRef.current = currentTime;
      saveCurrentTime();
    }
  }, [playerElement, saveCurrentTime]);

  return (
    <div
      className={classNames(classes['player-wrapper'], {
        [classes['player-wrapper--hidden']]: !src,
        [classes['player-wrapper--mobile']]: isMobileOnly,
      })}
    >
      <div className={classes['player']}>
        <div className={classes['player-title']} onClick={titleClickHandler}>
          {cardTitle}
        </div>

        <MediaPlayer
          {...(src && { src: `${src}?cardId=${cardId}` })}
          ref={playerRef}
          onPlay={onPlay}
          onPause={onPause}
          onSeeked={saveCurrentTime}
          onTimeUpdate={handleTimeUpdate}
          viewType={'audio'}
          autoPlay
          playsInline
        >
          <MediaProvider />
          <DefaultAudioLayout icons={defaultLayoutIcons} />
        </MediaPlayer>
      </div>

      <CloseButton
        onClick={onClose}
        ariaLabel={t('common.close')}
        className={classes['player-close']}
      />
    </div>
  );
};
